import type { UnconfirmedActionMessage } from "@engine/core";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { assertGetEvaluator, assertGetMember } from "./accessors";
import type { RoundAggregate } from "./aggregate";

export function isActionPermitted(
  round: RoundAggregate,
  actionMessage: UnconfirmedActionMessage,
): true | string {
  const action = actionMessage.payload;
  switch (action.type) {
    // Any user can add a round
    case "CREATE_ROUND":
      return true;

    // Round admins only:
    case "ADD_ROUND_MEMBER":
    case "ADD_PROPOSAL":
    case "ADD_PROPOSAL_FORM":
    case "ADD_OR_UPDATE_PROPOSAL_FORM_FIELD":
    case "ADD_EVALUATOR":
    case "REMOVE_ROUND_MEMBER":
    case "REMOVE_PROPOSAL_FORM":
    case "REMOVE_PROPOSAL_FORM_FIELD":
    case "REMOVE_EVALUATOR":
    case "REMOVE_PROPOSAL":
    case "PROPOSAL_FORM_FIELD_CHANGE":
    case "PROPOSAL_LONGNAME_CHANGE":
    case "PROPOSAL_SHORTNAME_CHANGE":
    case "PROPOSAL_FIELD_RESPONSE_CHANGE":
    case "PROPOSAL_FORM_EXTERNAL_SYNC":
    case "PROPOSAL_FIELD_TITLE_CHANGE":
    case "PROPOSAL_FIELD_DESCRIPTION_CHANGE":
    case "ROUND_NAME_CHANGE":
    case "SET_TRACK_SETTINGS":
    case "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY": {
      const member = assertGetMember(round, actionMessage.actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      return "User isn't a round admin";
    }

    case "ADD_TRANCHE":
    case "START_EVALUATION":
    case "REMOVE_TRANCHE":
    case "EVALUATION_MVF_CHANGE":
    case "EVALUATION_SET_ZERO":
    case "EVALUATION_STATUS_CHANGE":
    case "SET_EXPECTED_MATCHING_FRACTION":
    case "NORMALIZE_EVALUATIONS":
    case "SET_TRANCHE_HEIGHT":
    case "SET_TRANCHE_ALLOCATION":
    case "INCREMENTAL_EVALS_PUSH":
    case "INCREMENTAL_EVALS_UNDO":
    case "INCREMENTAL_EVALS_REDO":
    case "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE": {
      const member = assertGetMember(round, actionMessage.actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      const evaluator = assertGetEvaluator(round, action.evaluatorId);
      if (evaluator.memberId === actionMessage.actingAsUserId) {
        return true;
      }

      return "User isn't a round admin or the evaluator taking action";
    }

    // Round admins can remove a round
    case "ONE_TIME_ADMIN_ACTION_1":
    case "ONE_TIME_ADMIN_ACTION_2":
    case "REMOVE_ROUND": {
      const member = assertGetMember(round, actionMessage.actingAsUserId);

      // TODO(SP-629): This should be SFC admins
      if (member.isRoundAdmin) {
        return true;
      }

      return "User isn't an SFC admin";
    }

    // Round members can change feedback, notes, and their own properties
    case "EVALUATION_FEEDBACK_CHANGE":
    case "EVALUATION_NOTES_CHANGE":
    case "NOTES_AND_FEEDBACK_VISIBILITY_CHANGE":
    case "ROUND_MEMBER_PROPERTY_CHANGE": {
      const member = assertGetMember(round, actionMessage.actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      if (action.memberId === actionMessage.actingAsUserId) {
        return true;
      }

      return "User can't act on another user's feedback, notes, or properties";
    }

    case "EVALUATOR_PROPERTY_CHANGE": {
      const member = assertGetMember(round, actionMessage.actingAsUserId);

      if (!member) {
        return "Member not found";
      }

      if (member.isRoundAdmin) {
        return true;
      }

      const evaluator = assertGetEvaluator(round, action.evaluatorId);
      if (evaluator.memberId === actionMessage.actingAsUserId) {
        return true;
      }

      return "User can't act on another evaluator's properties";
    }

    // Round members can change their own investigation interest, review
    // progress, and disclosures
    case "INVESTIGATION_INTEREST_CHANGE":
    case "PROPOSAL_DISCLOSURE_CHANGE":
    case "REVIEW_PROGRESS_CHANGE": {
      const member = assertGetMember(round, actionMessage.actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      if (member.userId === actionMessage.actingAsUserId) {
        return true;
      }

      return "User can't act on another user's investigation interest, review progress, or disclosures";
    }

    // Round admins and funders only can change funder budgets and scores
    case "FUNDER_BUDGET_CHANGE":
    case "FUNDER_SETTLEMENT_BUDGET_CHANGE":
    case "FUNDER_SCORES_CHANGE": {
      const member = assertGetMember(round, actionMessage.actingAsUserId);
      if (member.isRoundAdmin) {
        return true;
      }

      const evaluator = assertGetEvaluator(round, action.funderId);
      if (evaluator.memberId === actionMessage.actingAsUserId) {
        return true;
      }

      return "User isn't a funder in this round";
    }

    // Track members can vote on recusals
    case "RECUSAL_VOTE": {
      const { votingUserId, subjectUserId } = action;
      const votingMember = assertGetMember(round, votingUserId);
      const subjectMember = assertGetMember(round, subjectUserId);

      if (subjectMember.track === "NO_TRACK") {
        return true;
      }

      if (subjectMember.track === votingMember.track) {
        return true;
      }

      return "User must be in the same track as the member whose recusal they are voting on";
    }

    default:
      assertUnreachable(action);
  }
}
